import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  scanOrderDocument, createOrderFromScan, getOrderClients, getOfferSuggestion,
} from '../services/orders';
import ClientPicker from '../components/common/ClientPicker';
import './OrderScanPage.css';

/** LAKPED-16.5 — pedido recibido: revisar, vincular con su oferta y confirmar.
 *
 *  El trabajo deja de escribirse en texto libre: va en una línea por artículo con su cantidad y su
 *  código (reunión del 28-10). Con el artículo se llega al plano y en el plano están las operaciones.
 */
const PASOS = ['Documento', 'Datos', 'Oferta vinculada', 'Confirmar'];

const LINEA_VACIA = { quantity: '', part_code: '', unit_price: '' };

const toDateInput = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};

const euros = (valor) => ((valor || valor === 0)
  ? Number(valor).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })
  : null);

const fecha = (valor) => (valor ? new Date(valor).toLocaleDateString('es-ES') : '');

const numeroOferta = (sugerencia) => {
  if (!sugerencia?.number) return sugerencia?.hash?.slice(0, 8) || '';
  const anio = new Date(sugerencia.created || Date.now()).getFullYear().toString().slice(-2);
  return `O${anio}/${String(sugerencia.number).padStart(4, '0')}`;
};

const OrderScanPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const [isDragOver, setIsDragOver] = useState(false);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [scan, setScan] = useState(null);
  const [form, setForm] = useState({});
  const [detectado, setDetectado] = useState({});
  const [lineas, setLineas] = useState([{ ...LINEA_VACIA }]);
  const [clients, setClients] = useState([]);
  const [analyzeDrawing, setAnalyzeDrawing] = useState(true);
  const [imagen, setImagen] = useState(null);
  const [sugerencia, setSugerencia] = useState(null);
  const [oferta, setOferta] = useState(null);
  const [descartada, setDescartada] = useState(false);

  useEffect(() => {
    getOrderClients().then(setClients).catch(() => setClients([]));
  }, []);

  // LAKPED-16.1: si se viene de la pantalla de clasificación el documento ya está leído,
  // así que se aprovecha en vez de volver a subirlo (crearía un segundo análisis).
  const aplicarEscaneo = (result) => {
    const data = result.order_data || {};
    setScan(result);
    setForm({
      cliente: data.cliente || '',
      numero_documento: data.numero_documento || '',
      fecha: toDateInput(data.fecha),
    });
    setDetectado({
      cliente: Boolean(data.cliente),
      numero_documento: Boolean(data.numero_documento),
      fecha: Boolean(data.fecha),
    });
    setLineas([{
      quantity: data.cantidad ?? '',
      part_code: data.articulo || '',
      unit_price: data.importe_total && data.cantidad
        ? String(Number(data.importe_total) / Number(data.cantidad))
        : '',
    }]);
    setAnalyzeDrawing((result.drawing_pages || []).length > 0);
  };

  useEffect(() => {
    if (location.state?.escaneo) aplicarEscaneo(location.state.escaneo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La primera página es el pedido escaneado: el visor de PDF del navegador lo enseña pequeño
  // y sobre fondo negro, así que se pide ya renderizada al servidor.
  useEffect(() => {
    const hash = scan?.analysis_hash;
    if (!hash) return undefined;
    let vivo = true;
    let url = null;
    fetch(`${process.env.REACT_APP_API_URL}/v1/analyses/${hash}/image?page=1`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(res => (res.ok ? res.blob() : Promise.reject(res.status)))
      .then(blob => {
        if (!vivo) return;
        url = URL.createObjectURL(blob);
        setImagen(url);
      })
      .catch(() => {});
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [scan]);

  const codigos = lineas.map(l => (l.part_code || '').trim()).filter(Boolean);
  const clienteElegido = clients.find(
    (c) => c.name.toLowerCase() === (form.cliente || '').trim().toLowerCase(),
  );

  const buscarOferta = useCallback(async () => {
    if (!codigos.length || !form.cliente) { setSugerencia(null); return; }
    try {
      setSugerencia(await getOfferSuggestion({
        codes: codigos,
        clientHash: clienteElegido?.hash,
        clientName: form.cliente,
      }));
    } catch (err) {
      setSugerencia(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigos.join('|'), form.cliente, clienteElegido?.hash]);

  useEffect(() => {
    if (!scan || oferta || descartada) return undefined;
    const t = setTimeout(buscarOferta, 400);
    return () => clearTimeout(t);
  }, [scan, oferta, descartada, buscarOferta]);

  const vincular = () => {
    setOferta(sugerencia);
    // Al vincular, el precio ofertado se recupera: muchas veces el pedido llega sin precio.
    setLineas(prev => prev.map(l => {
      const ofertado = sugerencia.prices?.[(l.part_code || '').trim()];
      return ofertado === undefined ? l : { ...l, offered_price: ofertado };
    }));
  };

  const readDocument = async (file) => {
    if (!file) return;
    setReading(true);
    setError(null);
    setScan(null);
    try {
      aplicarEscaneo(await scanOrderDocument(file));
    } catch (err) {
      const status = err.response?.status;
      setError(status === 503
        ? 'El servicio de lectura de documentos no está disponible. Inténtalo en unos minutos.'
        : 'No se ha podido leer el documento.');
    } finally {
      setReading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragOver(false);
    if (reading) return;
    readDocument(e.dataTransfer.files?.[0]);
  };

  // Cambiar el cliente o un artículo vuelve a abrir la búsqueda de oferta: lo que se descartó
  // era la propuesta anterior, no esta.
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDetectado((prev) => ({ ...prev, [key]: false }));
    if (key === 'cliente') setDescartada(false);
  };

  const updateLinea = (i, campo, valor) => {
    setLineas(prev => prev.map((l, idx) => (idx === i ? { ...l, [campo]: valor } : l)));
    if (campo === 'part_code') setDescartada(false);
  };

  const numero = (valor) => (valor === '' || valor === null || valor === undefined
    ? null : Number(valor));

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const order = await createOrderFromScan({
        analysis_hash: scan.analysis_hash,
        client_hash: clienteElegido?.hash || null,
        client_name: clienteElegido ? null : (form.cliente || '').trim() || null,
        client_reference: form.numero_documento || null,
        order_date: form.fecha ? `${form.fecha}T00:00:00` : null,
        offer_hash: oferta?.hash || null,
        lines: lineas
          .filter(l => (l.part_code || '').trim() || l.quantity)
          .map(l => ({
            part_code: (l.part_code || '').trim() || null,
            quantity: numero(l.quantity),
            unit_price: numero(l.unit_price),
            offered_price: numero(l.offered_price),
          })),
        analyze_drawing: analyzeDrawing,
      });
      navigate(`/orders/${order.hash}`);
    } catch (err) {
      setError('No se ha podido crear el pedido.');
      setSaving(false);
    }
  };

  const drawingPages = scan?.drawing_pages || [];
  const nothingFound = scan && !scan.order_data;

  const campo = (clave, etiqueta, tipo = 'text') => (
    <div className={`scan-field${detectado[clave] ? ' detectado' : ''}`}>
      <label htmlFor={`scan-${clave}`}>
        {etiqueta}
        {detectado[clave] && <em className="scan-badge">detectado</em>}
      </label>
      <input
        id={`scan-${clave}`}
        type={tipo}
        value={form[clave] ?? ''}
        onChange={(e) => updateField(clave, e.target.value)}
        placeholder={form[clave] ? '' : 'Sin leer'}
      />
    </div>
  );

  return (
    <div className="scan-container">
      {scan && (
        <ol className="scan-pasos">
          {PASOS.map((titulo, i) => {
            const hecho = i === 0 || (i === 2 && Boolean(oferta));
            return (
              <li key={titulo} className={hecho ? 'hecho' : (i === 1 ? 'actual' : '')}>
                <span className="scan-paso-num">{hecho ? '✓' : i + 1}</span>
                {titulo}
              </li>
            );
          })}
        </ol>
      )}

      {!scan && <button className="scan-back" onClick={() => navigate('/orders')}>‹ Volver a pedidos</button>}

      <h1 className="scan-title">{scan ? 'Pedido recibido · revisar y confirmar' : 'Nuevo pedido desde un documento'}</h1>
      {scan && (
        <p className="scan-sub">
          {scan.filename} · los campos azules vienen del documento y se pueden corregir.
        </p>
      )}

      {!scan && (
        <div
          className={`scan-dropzone${isDragOver ? ' drag-over' : ''}${reading ? ' reading' : ''}`}
          onDragEnter={(e) => { e.preventDefault(); dragCounter.current += 1; setIsDragOver(true); }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => { dragCounter.current -= 1; if (dragCounter.current <= 0) setIsDragOver(false); }}
          onDrop={handleDrop}
          onClick={() => !reading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            hidden
            onChange={(e) => readDocument(e.target.files?.[0])}
          />
          {reading ? (
            <>
              <div className="scan-spinner" />
              <p className="scan-dropzone-text">Leyendo el documento…</p>
              <p className="scan-dropzone-hint">Suele tardar unos segundos</p>
            </>
          ) : (
            <>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5-5 5 5M12 5v12" />
              </svg>
              <p className="scan-dropzone-text">Arrastra aquí el pedido escaneado</p>
              <p className="scan-dropzone-hint">o pulsa para elegirlo · PDF o imagen</p>
            </>
          )}
        </div>
      )}

      {error && <div className="scan-error">{error}</div>}

      {scan && (
        <div className="scan-columnas">
          <div>
            <div className="scan-doc">
              {imagen
                ? <img src={imagen} alt="Documento recibido" />
                : <div className="scan-doc-vacio">Cargando el documento…</div>}
              <div className="scan-doc-pie">
                {scan.total_pages} página{scan.total_pages === 1 ? '' : 's'}
                {drawingPages.length > 0 && ` · plano en la ${drawingPages.join(', ')}`}
                <button className="scan-change" onClick={() => { setScan(null); setForm({}); setLineas([{ ...LINEA_VACIA }]); }}>
                  Cambiar
                </button>
              </div>
            </div>

            {drawingPages.length > 0 && (
              <label className="scan-analyze">
                <input
                  type="checkbox"
                  checked={analyzeDrawing}
                  onChange={(e) => setAnalyzeDrawing(e.target.checked)}
                />
                <span>
                  Analizar también el plano de la página {drawingPages.join(', ')}
                  <span className="scan-analyze-hint">Se hace en segundo plano: el pedido se crea al momento.</span>
                </span>
              </label>
            )}
          </div>

          <div>
            {nothingFound && (
              <div className="scan-warning">
                No he reconocido ningún pedido en este documento. Puedes rellenar los datos a mano igualmente.
              </div>
            )}

            <div className="scan-tarjeta">
              <h2>Datos del pedido</h2>
              <div className="scan-form">
                <div className={`scan-field${detectado.cliente ? ' detectado' : ''}`}>
                  <label htmlFor="scan-cliente">
                    Cliente
                    {detectado.cliente && <em className="scan-badge">detectado</em>}
                  </label>
                  <ClientPicker
                    id="scan-cliente"
                    clients={clients}
                    value={form.cliente || ''}
                    onChange={(nombre) => updateField('cliente', nombre)}
                    placeholder="Nombre del cliente"
                  />
                </div>
                {campo('numero_documento', 'Nº de pedido del cliente')}
                {campo('fecha', 'Fecha', 'date')}
              </div>
            </div>

            <div className="scan-tarjeta">
              <h2>Líneas del pedido</h2>
              <table className="scan-lineas">
                <thead>
                  <tr>
                    <th className="der">Cant.</th>
                    <th>Artículo</th>
                    <th className="der">Precio pedido</th>
                    <th className="der">Precio ofertado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((linea, i) => (
                    <tr key={i}>
                      <td className="der">
                        <input
                          className="corto"
                          type="number"
                          value={linea.quantity ?? ''}
                          onChange={(e) => updateLinea(i, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          value={linea.part_code || ''}
                          onChange={(e) => updateLinea(i, 'part_code', e.target.value)}
                          placeholder="Código del artículo"
                        />
                      </td>
                      <td className="der">
                        <input
                          className="corto"
                          type="number"
                          step="0.01"
                          value={linea.unit_price ?? ''}
                          onChange={(e) => updateLinea(i, 'unit_price', e.target.value)}
                          placeholder="—"
                        />
                      </td>
                      <td className="der scan-ofertado">
                        {euros(linea.offered_price) || <span className="scan-muted">—</span>}
                      </td>
                      <td className="der">
                        {linea.offered_price !== undefined && (
                          <span className={`scan-chip${numero(linea.unit_price) === null ? ' naranja' : ''}`}>
                            {numero(linea.unit_price) === null
                              ? 'Se usa el ofertado'
                              : (Number(linea.unit_price) === Number(linea.offered_price) ? 'Coincide' : 'Distinto')}
                          </span>
                        )}
                        {lineas.length > 1 && (
                          <button
                            className="scan-quitar"
                            onClick={() => setLineas(prev => prev.filter((_, idx) => idx !== i))}
                            title="Quitar la línea"
                          >
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="scan-anadir" onClick={() => setLineas(prev => [...prev, { ...LINEA_VACIA }])}>
                + Añadir línea
              </button>
              <p className="scan-ayuda">
                Una línea por artículo. El precio puede no venir en el pedido: si se vincula una
                oferta nuestra, se recupera el que ofertamos.
              </p>
            </div>

            {oferta && (
              <div className="scan-tarjeta vinculada">
                <h2>Oferta vinculada</h2>
                <div className="scan-oferta-fila">
                  <div>
                    <b>{numeroOferta(oferta)}</b> · {euros(oferta.total)}
                    <span className="scan-muted"> · se ha recogido su precio en {oferta.matched_codes.length} línea
                      {oferta.matched_codes.length === 1 ? '' : 's'}</span>
                  </div>
                  <button className="scan-secundario" onClick={() => { setOferta(null); setDescartada(true); }}>
                    Desvincular
                  </button>
                </div>
              </div>
            )}

            {!oferta && sugerencia && (
              <div className="scan-tarjeta sugerida">
                <h2>Oferta relacionada encontrada</h2>
                <div className="scan-oferta-fila">
                  <div>
                    <b>{numeroOferta(sugerencia)}</b>
                    {sugerencia.sent_at ? ` · enviada el ${fecha(sugerencia.sent_at)}` : ' · sin enviar'}
                    {' · '}{euros(sugerencia.total)}
                    <p className="scan-muted">
                      Coincide por cliente y artículo en {sugerencia.matched_codes.length} línea
                      {sugerencia.matched_codes.length === 1 ? '' : 's'}: {sugerencia.matched_codes.join(', ')}.
                    </p>
                  </div>
                  <div className="scan-oferta-acciones">
                    <button className="scan-secundario" onClick={() => { setSugerencia(null); setDescartada(true); }}>
                      No es esta
                    </button>
                    <button className="scan-principal" onClick={vincular}>Vincular</button>
                  </div>
                </div>
              </div>
            )}

            <div className="scan-actions">
              <button className="scan-cancel" onClick={() => navigate('/orders')} disabled={saving}>Cancelar</button>
              <button className="scan-confirm" onClick={handleConfirm} disabled={saving}>
                {saving ? 'Creando…' : 'Confirmar y crear pedido'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderScanPage;
